import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { btnPrimary, btnSecondary } from '@/components/rm'
import { useAuth } from '@/hooks/use-auth'
import { LanguageMenu, useT } from '@/i18n'
import { displayPhMobile } from '@/lib/ph'
import { hasCompany, setMode, useProfile, useSaveProfile } from '@/lib/account'

// Spec 002 US4. Name, mobile number (read only: it is the sign-in), language, and the door to the
// business side.
export const Route = createFileRoute('/_client/profile')({ component: ClientProfile })

function ClientProfile() {
  const { t } = useT()
  const { session } = useAuth()
  const navigate = useNavigate()
  const profile = useProfile()
  const save = useSaveProfile()
  const [name, setName] = useState('')
  useEffect(() => { if (profile.data) setName(profile.data.display_name ?? '') }, [profile.data])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const n = name.trim().replace(/\s+/g, ' ')
    if (!n) return
    await save.mutateAsync({ display_name: n.slice(0, 80) })
    toast.success(t('profile.saved'))
  }

  const openBusiness = async () => {
    if (!hasCompany(session)) return navigate({ to: '/offer-services' })
    await setMode('business')
    navigate({ to: '/dashboard' })
  }

  return (
    <div className="space-y-5">
      <h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">{t('profile.title')}</h1>
      <form onSubmit={submit} className="space-y-4 rounded-[16px] border border-token-subtle bg-token-surface p-5">
        <div className="space-y-1.5">
          <label htmlFor="pname" className="text-[13px] font-semibold text-token-secondary">{t('profile.name')}</label>
          <input id="pname" value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
            className="h-11 w-full rounded-[10px] border border-token-subtle px-3 text-[15px] text-token-primary outline-none focus:border-token-strong" />
        </div>
        <div className="space-y-1">
          <p className="text-[13px] font-semibold text-token-secondary">{t('profile.mobile')}</p>
          <p className="text-[15px] text-token-primary">{profile.data?.mobile ? displayPhMobile(profile.data.mobile) : <span className="text-[13px] text-token-warning">{t('profile.mobileNone')}</span>}</p>
        </div>
        <button type="submit" disabled={save.isPending} className={`${btnPrimary} h-10`}>{save.isPending ? t('profile.saving') : t('profile.save')}</button>
      </form>

      <section className="flex flex-col gap-3 rounded-[16px] border border-token-subtle bg-token-surface p-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[15px] font-bold text-token-primary">{t('account.languageTitle')}</p>
        <LanguageMenu />
      </section>

      <section className="space-y-3 rounded-[16px] border border-token-subtle bg-token-surface p-5">
        <p className="text-[15px] font-bold text-token-primary">{t('profile.business')}</p>
        <p className="text-[13.5px] text-token-secondary">{t('profile.businessBody')}</p>
        <button type="button" onClick={openBusiness} className={`${btnSecondary} h-10`}>
          {hasCompany(session) ? t('profile.openBusiness') : t('client.mode.offer')}
        </button>
      </section>
    </div>
  )
}
